<template>
  <div
    v-show="isCustomFieldValid"
    :class="{ 'phone-field--table-cell': props.isTableCell }"
  >
    <label
      v-if="!props.hideLabel"
      class="input-label tw-flex tw-items-center"
      :for="`${customField?.name}`"
    >
      {{ customField?.name }}
      <span
        v-if="props.customField.required"
        class="tw-ml-0.5 tw-text-sm tw-text-red"
      >
        *
      </span>
      <help-text v-if="!!customField.description" class="tw-mb-0.5 tw-ml-1">
        {{ customField.description }}
      </help-text>
    </label>
    <phone-input
      v-if="mounted"
      id="phone"
      :key="
        // Adding this key fixes an issue where if the props.value is cleared (by an automation for example), the phone number would not clear
        String(!!props.value)
      "
      ref="phoneInput"
      v-model="formattedPhoneNumber"
      :status="!phoneNumberValid && !!errorMessage ? 'error' : 'success'"
      :readonly="props.readonly"
      :is-table-cell="props.isTableCell"
      :autofocus="props.autofocus"
      @validation-changed="updatePhoneValidation"
      @blur="onBlur"
      @enter="handleEnter"
      @escape="handleEscape"
    />
    <span
      v-if="!!errorMessage && showErrorMessage"
      class="form-input__error-message"
    >
      {{ errorMessage }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import HelpText from '../design-system/misc/HelpText.vue';
import PhoneInput from '@/components/core/PhoneInput.vue';
import { timeout } from '@/utils/time';
import { CustomField, CustomFieldTextSubType, CustomFieldType } from '@/api';

interface Props {
  customField: CustomField & { required?: boolean };
  value?: string;
  hideLabel?: boolean;
  customError?: string;
  readonly?: boolean;
  isTableCell?: boolean;
  autofocus?: boolean;
}
interface Emits {
  (e: 'value-updated', value: string | null): void;
  (e: 'enter'): void;
  (e: 'escape'): void;
}

const props = withDefaults(defineProps<Props>(), {});
const emit = defineEmits<Emits>();

const mounted = ref(false);
onMounted(async () => {
  await timeout(0); // fixes an issue where the formattedPhoneNumber gets overwritten
  mounted.value = true;
});

const phoneInput = ref<typeof PhoneInput | null>(null);

const isCustomFieldValid = computed(() => {
  return (
    props.customField.type === CustomFieldType.TEXT &&
    props.customField.subType === CustomFieldTextSubType.PHONE_NUMBER
  );
});

const formattedPhoneNumber = ref<string>(props.value || '');
watch(
  () => props.value,
  () => {
    formattedPhoneNumber.value = props.value || '';
  },
  { immediate: true }
);

const showErrorMessage = ref(false);
watch(
  () => props.customError,
  () => {
    showErrorMessage.value = !!props.customError;
  }
);
const phoneNumberValid = ref(false);

const updatePhoneValidation = (valid: boolean) => {
  phoneNumberValid.value = valid;
};

const errorMessage = computed(() => {
  if (props.customError) {
    return props.customError;
  }
  if (!formattedPhoneNumber.value && props.customField.required) {
    return 'This field is required';
  }
  if (!phoneNumberValid.value && formattedPhoneNumber.value)
    return 'Invalid phone number';
  return '';
});

watch(formattedPhoneNumber, () => {
  showErrorMessage.value = false;
});

function onBlur() {
  if (
    props.customField.required &&
    (!formattedPhoneNumber.value || !phoneNumberValid.value)
  ) {
    formattedPhoneNumber.value = props.value || '';
    return;
  }
  onValueChange();
}

function onValueChange() {
  if (errorMessage.value) {
    showErrorMessage.value = true;
    return;
  }
  if (
    formattedPhoneNumber.value === props.value ||
    (!formattedPhoneNumber.value && !props.value)
  ) {
    return;
  }
  emit('value-updated', formattedPhoneNumber.value);
}

const isValid = computed(() => {
  return !errorMessage.value;
});

function validate() {
  showErrorMessage.value = !isValid.value;
}

function removeError() {
  showErrorMessage.value = false;
}

function handleEnter() {
  // Emit the current value before emitting enter
  if (formattedPhoneNumber.value !== props.value) {
    emit('value-updated', formattedPhoneNumber.value);
  }
  emit('enter');
}

function handleEscape() {
  formattedPhoneNumber.value = props.value || '';
  emit('escape');
}

defineExpose({
  validate,
  removeError,
  isValid,
  value: formattedPhoneNumber,
  phoneInput,
  focus: () => phoneInput.value?.focus(),
});
</script>

<style lang="scss" scoped>
.phone-field--table-cell {
  :deep(.phone-input) {
    @apply tw-leading-none tw-text-sm;
    padding-top: 0;
    padding-bottom: 0;
  }

  :deep(.phone-input__wrapper-inner) {
    @apply tw-border-0;
    min-height: auto;
  }

  :deep(.iti.iti--allow-dropdown) {
    min-height: auto;
  }

  :deep(.iti__flag-container) {
    padding: 0;
  }
}
</style>
